import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { types as sdkTypes } from './sdkLoader';
import { formatMoney, convertPrice } from './currency';
import { getRentalPackage, userDisplayNameAsString } from './data';
import { generateStoreSlug, generateCitySlug } from './seo';

const { Money } = sdkTypes;

// Shared by CartDrawerItem.js and AllCartItemsPanel.js - both show a small circular
// item thumbnail and were previously each hand-rolling their own copy of this variant
// list plus their own src-selection logic. getItemsByAuthor (cart.duck.js) already
// requests exactly these variants when denormalizing cart items, so they're always
// available on item.listing.images[0] without an extra fetch.
export const THUMBNAIL_IMAGE_VARIANTS = ['square-small', 'square-small2x'];

/**
 * Build fetch line items payload from cart items
 * @param {Array} items - Cart items for an author
 * @returns {Array} fetchLineItemsData payload
 */
export const buildFetchLineItemsPayload = (items = []) => {
  return items.map(item => {
    const {
      bookingDates,
      timeZone,
      quantity,
      package: selectedPackage,
      listingId,
      listing,
      usageMultipleValues = {},
      selectedFeeTypes = [],
      pickupAtOwnerLocation = false,
      returnAtOwnerLocation = false,
    } = item;

    const startDate = moment(bookingDates.startDate).tz(timeZone).toDate();
    const endDate = moment(bookingDates.endDate).tz(timeZone).toDate();
    const currentPackage = getRentalPackage({ bookingStart: startDate, bookingEnd: endDate, selectedPackage, pricePackages: listing?.attributes?.publicData?.prices });

    return {
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        seats: parseInt(quantity, 10),
        package: currentPackage || selectedPackage,
        usageMultipleValues,
        selectedFeeTypes,
        pickupAtOwnerLocation,
        returnAtOwnerLocation,
      },
      listingId: listingId || listing?.id?.uuid,
      isOwnListing: false,
    };
  });
};

/**
 * Calculate subtotal from line items with currency conversion
 * @param {Array} lineItems - Array of line items
 * @param {String} currency - Target currency code
 * @param {Object} intl - React Intl object
 * @param {Array} exchangeRates - Exchange rates for currency conversion
 * @returns {String} Formatted subtotal amount
 */
export const calculateSubTotal = (lineItems, currency, intl, exchangeRates = []) => {
  if (!lineItems?.length) return null;

  const subTotalAmount = lineItems
    .filter(({ includeFor }) => includeFor[0] === 'customer' && includeFor[1] === 'provider')
    .reduce((total, item) => {
      // Convert each line item to user's currency before summing
      const fromCurrency = item.lineTotal.currency;
      const convertedPrice = convertPrice(
        exchangeRates,
        item.lineTotal.amount,
        fromCurrency,
        currency
      );
      return total + (convertedPrice?.amount || 0);
    }, 0);

  return formatMoney(intl, new Money(subTotalAmount, currency));
};

/**
 * Calculate full customer total from line items (including platform fees).
 * Sums all line items where includeFor contains 'customer'.
 */
export const calculateCustomerTotal = (lineItems, currency, intl, exchangeRates = []) => {
  if (!lineItems?.length) return null;

  const totalAmount = lineItems
    .filter(({ includeFor }) => Array.isArray(includeFor) && includeFor.includes('customer'))
    .reduce((total, item) => {
      const fromCurrency = item.lineTotal.currency;
      const convertedPrice = convertPrice(
        exchangeRates,
        item.lineTotal.amount,
        fromCurrency,
        currency
      );
      return total + (convertedPrice?.amount || 0);
    }, 0);

  return formatMoney(intl, new Money(totalAmount, currency));
};


/**
 * RAS-2520: picks which cart group the listing page's slide-out drawer should show.
 *
 * Extracted as a pure function on purpose. Every bug reported against this drawer so far
 * has been in the WIRING - which booking it was asked to display - not in the grouping or
 * pricing helpers underneath it, and those helpers were the only part under test. This is
 * the decision itself, so it can be tested directly.
 *
 * `scope` is the booking the user actually acted on: the one whose Rent Now they pressed,
 * or the row they picked in the all-items panel. Without it the caller can only guess,
 * and the guess used to be "the most recently added group" - which is right when the user
 * has just added something new, and wrong whenever they press Rent Now on a booking that
 * already exists and is older than some other group in their cart. That is exactly how an
 * overnight booking opened the drawer on a 2-day daily booking of the same listing.
 *
 * Falls back to the most recent group only when there is no scope at all.
 *
 * @param {Array<Object>} groups - from groupCartItemsByAuthorAndDateRange, newest first
 * @param {{startDate: *, endDate: *}|null} scope - the acted-on booking's date range
 * @returns {Object|null} the matching group, or null when a scope matches nothing
 */
export const selectCartDrawerGroup = (groups, scope) => {
  if (!groups || groups.length === 0) {
    return null;
  }
  if (!scope?.startDate || !scope?.endDate) {
    return groups[0];
  }
  return (
    groups.find(
      group =>
        moment(group.startDate).isSame(scope.startDate, 'day') &&
        moment(group.endDate).isSame(scope.endDate, 'day')
    ) || null
  );
};

/**
 * Which cart surface the store page opens when its cart is requested (nav-bar cart
 * button, ?storeCart=open, add-to-cart auto-open), and which group it shows.
 *
 * The Store Cart panel holds exactly ONE (store, date range) group - same contract as
 * the listing page's drawer. The group is whichever one matches `scope`: an explicit
 * scope (a row tapped in the all-orders panel, the booking just added) or, absent
 * that, the page's current date filter. When nothing matches - the shopper corrected
 * their dates after adding items, has no dates selected, or only has items at other
 * stores - the all-orders panel opens instead, so they always land on a list of what
 * they actually have rather than an empty panel and a dead button. A completely empty
 * cart still opens the panel, whose empty state says so.
 *
 * Pure and exported so the decision itself is unit-tested, not just the pieces
 * (same reasoning as selectCartDrawerGroup above).
 *
 * @param {Array<Object>} storeGroups - this store's groups (groupCartItemsByAuthorAndDateRange)
 * @param {{startDate: *, endDate: *}|null} scope - explicit override or the page's date filter
 * @param {boolean} hasAnyCartItems - whether the whole cart (every store) holds anything
 * @returns {{view: 'cart'|'allOrders', group: Object|null}}
 */
export const resolveStoreCartView = (storeGroups, scope, hasAnyCartItems) => {
  const hasScopeDates = !!(scope?.startDate && scope?.endDate);
  const group = hasScopeDates ? selectCartDrawerGroup(storeGroups, scope) : null;
  if (group) {
    return { view: 'cart', group };
  }
  return hasAnyCartItems ? { view: 'allOrders', group: null } : { view: 'cart', group: null };
};

/**
 * A cart author's display label: the store's name for store accounts, the person's
 * display name for individuals. One implementation for every cart surface (drawer,
 * all-orders panel, different-owner popup) so they can never name the same store two
 * different ways. Returns '' when there is no usable author (e.g. the denormalized
 * listing on a cart item is gone because the listing was deleted).
 *
 * @param {Object|null} author - a denormalized user entity (item.listing.author)
 * @returns {string}
 */
export const getCartAuthorStoreName = author => {
  if (!author) return '';
  const storeName = author?.attributes?.profile?.publicData?.storeName;
  return storeName || userDisplayNameAsString(author, '');
};

/**
 * Route name + params for a store's profile page, derived from the store's own user
 * entity - the same slug/city rule StoreProfilePage redirects legacy UUID-only URLs
 * to, so links built here never bounce through that client-side redirect (a full
 * page reload). One implementation for every cart surface that links to a store: the
 * cart page's store header and "Rent more from this store", and the store page's
 * all-carts panel when a row belongs to another store.
 *
 * Returns null without a usable author id; callers fall back to whatever they did
 * before (or hide the link).
 *
 * @param {Object|null} author - a user entity (item.listing.author or a page's profileUser)
 * @returns {{name: 'ProfilePage'|'ProfilePageNoCity', params: Object}|null}
 */
export const getStorePageRouteParams = author => {
  const authorId = author?.id?.uuid;
  if (!authorId) return null;
  const profile = author?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const citySlug = generateCitySlug(publicData.storeAddress?.city || publicData.city);
  const slug = generateStoreSlug(publicData.storeName || profile.displayName);
  return citySlug
    ? { name: 'ProfilePage', params: { city: citySlug, slug, id: authorId } }
    : { name: 'ProfilePageNoCity', params: { slug, id: authorId } };
};

// state.cart.items is keyed by authorId. A stored item that somehow lost its authorId
// (hand-edited profile data - addToCart has always required one) lands under a
// stringified "undefined"/"null" key; every reader below skips those so a corrupt row
// can neither trigger nor suppress the popup.
const isRealAuthorKey = authorId => !!authorId && authorId !== 'undefined' && authorId !== 'null';

/**
 * RAS-2622: the most recently added cart item across every store, or null when the
 * cart is empty / not loaded.
 *
 * Author keys are ordered most-recent-first and each author's items newest-first (both
 * by cart.duck.js's getItemsByAuthor). Items without an `addedAt` (added before the
 * timestamp existed) sort last there, so the newest timestamped item wins - exactly
 * what the popup's "In your cart" card wants.
 *
 * `preferredAuthorIds` (the stores added to in this session) narrows the search to
 * those stores when any of them still has items, so the card shows the cart the
 * shopper just started rather than an older one from a previous visit. Falls back
 * to the overall newest item when none of them has anything left.
 *
 * @param {Object|null} itemsByAuthor - state.cart.items
 * @param {Array<string>} [preferredAuthorIds]
 * @returns {Object|null}
 */
export const getMostRecentCartItem = (itemsByAuthor, preferredAuthorIds = []) => {
  if (!itemsByAuthor) return null;
  const firstItemFrom = authorIds => {
    for (const authorId of authorIds) {
      if (!isRealAuthorKey(authorId)) continue;
      const first = itemsByAuthor[authorId]?.[0];
      if (first) return first;
    }
    return null;
  };
  const allAuthorIds = Object.keys(itemsByAuthor);
  const preferred = allAuthorIds.filter(id => preferredAuthorIds.includes(id));
  return firstItemFrom(preferred) || firstItemFrom(allAuthorIds);
};

/**
 * RAS-2622: should adding a listing owned by `incomingAuthorId` show the "different
 * owner" confirmation popup?
 *
 * True only when adding would create a brand-new per-store cart NEXT TO ONE STARTED
 * IN THIS SESSION: the cart holds nothing from this owner, and holds at least one
 * item from some other owner whose id is in `sessionAuthorIds` - the stores added to
 * in this browser session (getCartAuthorsAddedThisSession, util/sessionStorageManager).
 * A cart left over from a previous visit doesn't qualify (client clarification
 * 2026-09-18): the popup explains a split the shopper is creating right now, not one
 * they may have forgotten about. Adding to an owner that already has items just joins
 * that owner's existing cart, so it stays quiet too.
 *
 * A not-yet-loaded cart (`null`, the state before fetchCartItems answers) is treated
 * as empty: an unknown cart must never block an add, and the worst case is simply
 * today's behavior.
 *
 * Pure and exported so the decision itself is unit-tested (same reasoning as
 * selectCartDrawerGroup above).
 *
 * @param {Object|null} itemsByAuthor - state.cart.items
 * @param {string} incomingAuthorId
 * @param {Array<string>} sessionAuthorIds - stores added to in this session
 * @returns {boolean}
 */
export const shouldPromptForDifferentOwner = (itemsByAuthor, incomingAuthorId, sessionAuthorIds = []) => {
  if (!itemsByAuthor || !incomingAuthorId) return false;
  if (itemsByAuthor[incomingAuthorId]?.length > 0) return false;
  return Object.keys(itemsByAuthor).some(
    authorId =>
      isRealAuthorKey(authorId) &&
      authorId !== incomingAuthorId &&
      sessionAuthorIds.includes(authorId) &&
      itemsByAuthor[authorId]?.length > 0
  );
};

/**
 * Which items of a cart have EXPIRED rental dates - a start day already in the past,
 * judged in the booking's own zone at day granularity (a booking starting today is
 * still valid, matching the calendars' own isOutsideRange rule).
 *
 * Shared by the cart page (per-card "Update Dates"), both side carts' expired notice,
 * and startCheckoutFromItems' guard, so every surface agrees on what "expired" means.
 *
 * @param {Array} items - the cart's items
 * @returns {{hasExpiredDates: boolean, expiredItems: Array}}
 */
export const checkExpiredRentalDates = items => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return { hasExpiredDates: false, expiredItems: [] };
  }

  const now = moment();
  const expiredItems = items.filter(item => {
    const { bookingDates, timeZone } = item;
    if (!bookingDates?.startDate) return false;

    const startDate = moment.tz(bookingDates.startDate, timeZone || 'Etc/UTC');
    return startDate.isBefore(now, 'day');
  });

  return {
    hasExpiredDates: expiredItems.length > 0,
    expiredItems,
  };
};
